
<script setup>
import { ref, onMounted, onUnmounted, computed, watch } from 'vue'

import { useRoute } from 'vue-router'
const route = useRoute()
// Beobachtet Änderungen am Filter-Query-Parameter.
// Die eigentliche Datennachladung erfolgt im Router-Guard; hier wird nur
// optional ein JSON-basierter Filter unterstützt, ohne bei Signaturen/Pipe-Strings Fehler zu loggen.
// Watcher auf route.query.filter entfernt, um doppelte Requests zu verhindern.

import Reminder from '@/components/Reminder.vue'
import BusinessPartnerFilter from '@/components/BusinessPartnerFilter.vue'
import userAvatar from '@/components/userAvatar.vue'
import PreviewHeader from '@/components/PreviewHeader.vue';
import FilterSummaryChips from '@/components/FilterSummaryChips.vue';

// DEBUG: Logge die Query und den Filter beim Mounten
// import { onMounted } from 'vue';
// import { useRoute } from 'vue-router';
// const route = useRoute();
// onMounted(() => {
//   if (typeof window !== 'undefined') {
//     console.log('[Post] BusinessPartnerList.vue mounted: $route.query', route.query);
//     if (route.query && route.query.filter) {
//       let parsed = route.query.filter;
//       try { parsed = JSON.parse(route.query.filter); } catch(e) {}
//       console.log('[Post] BusinessPartnerList.vue mounted: filter param', parsed);
//       // PATCH: Filter übernehmen!
//       if (parsed && typeof parsed === 'object' && parsed.businessPartnerSalesPitchStatus) {
//         userStore.currentSelection.businessPartnerList.filter = parsed;
//         // PATCH: Setze isUserFilter auf true, wenn Filter aus Route übernommen wird
//         userStore.currentSelection.businessPartnerList.isUserFilter = true;
//       }
//     }
//   }
// });

import { useUserStore } from '@/stores/user'
const userStore = useUserStore();
// Highlight helper: wraps matched query in <mark> and supports search from route or filter
function escapeHtml(str){
  return String(str).replace(/[&<>"]/g, (c)=> ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;'}[c]));
}
function highlightText(text){
  const searchObj = userStore.currentSelection?.businessPartnerList?.search || {};
  const filterSearch = userStore.currentSelection?.businessPartnerList?.filter?.search || {};
  const q = searchObj.query || searchObj.string || filterSearch.string || '';
  const mode = searchObj.mode || userStore.currentSelection?.businessPartnerList?.searchMode || filterSearch.mode || 'name';
  const wholeWord = (searchObj.wholeWord === 1 || searchObj.wholeWord === true || filterSearch.wholeWord === 1 || filterSearch.wholeWord === true);
  if (!text || !q) return escapeHtml(text || '');
  try {
    const escaped = q.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    // Mode-aware pattern: name uses word boundaries with letters, GPNo/tel use simple substring
    const pattern = (mode === 'name' && wholeWord)
      ? `(?<![A-Za-zÄÖÜäöüß])(${escaped})(?![A-Za-zÄÖÜäöüß])`
      : `(${escaped})`;
    const re = new RegExp(pattern, 'gi');
    const safe = escapeHtml(text);
    return safe.replace(re, (m, p1)=> `<mark>${p1 || m}</mark>`);
  } catch(e){
    return escapeHtml(text);
  }
}

function getColleagueById({ idUser, salesAgentId }) {
  if (!userStore.colleagues) return null;
  // Suche nach idUser
  let found = userStore.colleagues.find(
    c => c?.value && c.value.id === idUser
  );
  if (found && found.value) return found.value;
  // Suche nach salesAgentId
  found = userStore.colleagues.find(
    c => c?.value && c.value.isSalesAgent && c.value.isSalesAgent.id === salesAgentId
  );
  if (found && found.value) return found.value;
  return null;
}

function getColleagueName({ idUser, salesAgentId }) {
  const colleague = getColleagueById({ idUser, salesAgentId });
  if (!colleague) return salesAgentId ? `Agent ${salesAgentId}` : idUser ? `User ${idUser}` : 'Unbekannt';
  if (colleague.firstName) return `${colleague.firstName} ${colleague.lastName}`;
  return colleague.lastName || (salesAgentId ? `Agent ${salesAgentId}` : idUser ? `User ${idUser}` : 'Unbekannt');
}

function getSalesManagersWithStatus(bp) {
  const result = [];
  if (!bp || !Array.isArray(bp.lists)) return result;
  const seen = new Set();
  for (const list of bp.lists) {
    if (!list || !Array.isArray(list.colleaguesWithAccess)) continue;
    for (const col of list.colleaguesWithAccess) {
      const key = col?.idSalesAgent || col?.id;
      if (!key || seen.has(key)) continue;
      seen.add(key);
      const name = col?.name || getColleagueName({ idUser: col.id, salesAgentId: col.idSalesAgent });
      const statusLabel = col?.salesPitch?.status?.name || bp?.status?.listAs || 'Kein Status';
      result.push({
        idUser: col.id,
        idSalesAgent: col.idSalesAgent,
        name,
        statusLabel,
      });
    }
  }
  // Wenn ein einzelner Sales Manager als Scope ausgewählt ist, soll er in der Liste immer oben stehen
  try {
    if (userStore.currentSelection.scope === 'salesAgent' && userStore.currentSelection.salesAgent?.id) {
      const selfId = userStore.currentSelection?.salesAgent?.id;
      const idx = result.findIndex(sm => sm.idSalesAgent === selfId);
      if (idx > 0) {
        const [self] = result.splice(idx, 1);
        result.unshift(self);
      }
    }
  } catch (e) {
    // falls userStore unerwartet nicht verfügbar ist, einfach ursprüngliche Reihenfolge nutzen
  }
  return result;
}

import { useIncentivesStore } from '@/stores/incentives'
const incentivesStore = useIncentivesStore();

import { useBusinessPartnersStore } from '@/stores/businessPartners'
const businessPartnersStore = useBusinessPartnersStore();

import { useRatingsStore } from '@/stores/ratings';
const ratingsStore = useRatingsStore();

import CountdownBadge from '@/components/CountdownBadge.vue';

// Router instance for manual navigation after prefetch
import router from '@/router/routes';

const businessPartnerList = ref(null)

// Globales Lade-Overlay nur anzeigen, solange noch keine Listendaten vorhanden sind
// und die Route NICHT explizit aus dem Filter kommt (source=filter).
const shouldShowGlobalLoadingOverlay = computed(() => {
  const sel = userStore.currentSelection || {};

  const hasAnyLoading =
    sel.campaign?.isLoading === true ||
    userStore.filterOptions?.isLoading === true ||
    sel.businessPartner?.isLoading === true ||
    sel.businessPartnerList?.isLoading === true;

  const listData = businessPartnersStore.businessPartnerList?.data;
  const hasListData = Array.isArray(listData) && listData.length > 0;

  const source = route?.query?.source || '';
  const comesFromFilter = String(source).toLowerCase() === 'filter';

  return hasAnyLoading && !hasListData && !comesFromFilter;
});

// Zusätzlicher Overlay-Zustand, wenn ein Kunde aus der Liste
// geöffnet wird (Details-View). Nutzt den vorhandenen Spinner,
// zeigt aber einen separaten Text an.
const isOpeningBusinessPartner = computed(() => {
  return userStore.currentSelection?.businessPartner?.isLoading === true;
});

// Separater Ladezustand nur für "Mehr laden" (weitere Seite)
const isLoadingMore = ref(false);

// Deutlicher Hinweis, wenn die Liste mit einem neuen Filter neu geladen wird,
// obwohl bereits Daten angezeigt werden (Reload-Fall statt Erstladen).
const isReloadingList = computed(() => {
  // Wenn explizit "Mehr laden" aktiv ist, soll NICHT der Filter-Reload-Text erscheinen
  if (isLoadingMore.value) return false;

  const sel = userStore.currentSelection?.businessPartnerList;
  const isLoading = sel?.isLoading === true;

  const listData = businessPartnersStore.businessPartnerList?.data;
  const hasListData = Array.isArray(listData) && listData.length > 0;

  const source = route?.query?.source || '';
  const comesFromFilter = String(source).toLowerCase() === 'filter';

  return isLoading && hasListData && comesFromFilter;
});

// infinite scroll excample from https://learnvue.co/articles/vue-infinite-scrolling
// this gets the container of the businessPartners that should react to scrolling (similar to a querySelector) but by using ref
onMounted(async () => {
  window.addEventListener("scroll", handleScroll)

  if (userStore.currentSelection.businessPartnerList) {
    userStore.currentSelection.businessPartnerList.layout = 'list';
    const filter = userStore.currentSelection.businessPartnerList.filter;
    // Nur einen Default setzen, wenn noch kein expliziter Zustand vorhanden ist.
    // Router (hideFilter) oder vorherige Navigation sollen nicht überschrieben werden.
    if (filter && typeof filter.show !== 'boolean') {
      filter.show = (userStore.selectedMedia === 'desktop');
    }
  }

  setTimeout(() => {
    scrollToTarget();
    // Restore and apply last visited highlight when returning from details
    try { businessPartnersStore.restoreLastVisited?.(); } catch(e){}
    setTimeout(() => applyRecentFocus(), 100);
  }, 100); // Wartezeit, um sicherzustellen, dass das Layout vollständig gerendert ist

});

onUnmounted(() => {
  window.removeEventListener("scroll", handleScroll)
})

async function handleScroll(e) {
  
  return false;
  // dont react to scrolling if the list is loading
  if (userStore.currentSelection.businessPartnerList.isLoading) return false
  // if the user scrolls to the bottom of the page, see if the list needs to be expanded
  let element = businessPartnerList.value
  if (element.getBoundingClientRect().bottom < window.innerHeight) {
    await requestNewPage()
  }
}

const scrollContainer = ref(null);

// Merker, welche Seite zuletzt per "Mehr laden" neu hinzugekommen ist
const latestNewPage = ref(null);

function scrollToTarget() {
  const businessPartnerGpNo = route.query.businessPartnerGpNo;
  const position = route.query.position;
  const targetContainer = document.querySelector('.scroll-y');

  let target = null;
  if (businessPartnerGpNo) {
    target = document.getElementById(businessPartnerGpNo);
  }
  // Falls gpNo nicht gefunden, versuche es mit position
  if (!target && position) {
    target = document.getElementById(position);
  }

  if (!target || !targetContainer) {
    console.warn('Target or scroll container not found');
    return;
  }

  // Berechne die Position des Ziels relativ zum Container
  const targetPosition = target.offsetTop - targetContainer.offsetTop;

  // Passe das Offset basierend auf dem Layout an
  const layout = userStore.currentSelection.businessPartnerList.layout;
  const offset = layout === 'grid' ? 20 : 10;

  targetContainer.scrollTo({
    top: targetPosition - offset,
    behavior: 'smooth',
  });

  // Hervorheben des Ziels
  target.classList.add('highlight');
}

// toggle between list and grid layout
function handleToggleLayout() {
  const layout = userStore.currentSelection.businessPartnerList.layout;

  if (layout === 'list') {
    userStore.currentSelection.businessPartnerList.layout = 'grid';
  } else {
    userStore.currentSelection.businessPartnerList.layout = 'list';
  }

  // Warte, bis das Layout aktualisiert ist, und scrolle dann
  setTimeout(() => {
    scrollToTarget();
  }, 100); // Wartezeit, um sicherzustellen, dass das Layout vollständig gerendert ist
}

// open the filter
function handleOpenFilter() {
  userStore.currentSelection.businessPartnerList.filter.isOpen = !userStore.currentSelection.businessPartnerList.filter.isOpen
}

// get more data on clicking the "Mehr laden" button
async function requestNewPage() {
  if (isLoadingMore.value) return;
  // Vor dem Laden: aktuelle Anzahl und Seitengröße merken
  const prevCount = businessPartnersStore.businessPartnerList?.data?.length || 0;
  const pageSize = userStore.currentSelection.businessPartnerList.pageSize || 0;

  try {
    isLoadingMore.value = true;
    // Nur weitere Daten laden – kein automatisches Scrollen, damit die Ansicht stabil bleibt
    await businessPartnersStore.getByFilterExpand();

    // Nach dem Laden: neu hinzugekommene Seite markieren
    const newCount = businessPartnersStore.businessPartnerList?.data?.length || 0;
    if (pageSize > 0 && newCount > prevCount) {
      const prevPage = Math.ceil(prevCount / pageSize) || 0;
      const newPage = Math.ceil(newCount / pageSize);
      if (newPage > prevPage) {
        latestNewPage.value = newPage;
        // Markierung nach einigen Sekunden automatisch ausblenden
        setTimeout(() => {
          if (latestNewPage.value === newPage) latestNewPage.value = null;
        }, 8000);
      }
    }
  } finally {
    isLoadingMore.value = false;
  }
}

function isNewlyLoadedPage(pageNumber) {
  return latestNewPage.value === pageNumber;
}

// Apply a temporary visual focus to the last visited row/card
function applyRecentFocus(){
  const lv = businessPartnersStore.lastVisited;
  if(!lv || !lv.id) return;
  // Try to find row by data-bpid (preferred) or gpNo id fallback
  let row = document.querySelector(`[data-bpid="${lv.id}"]`);
  if(!row && lv.gpNo){ row = document.getElementById(String(lv.gpNo)); }
  if(!row) return;
  try {
    row.classList.add('recent-focus');
    setTimeout(()=> row.classList.add('recent-focus-fade'), 2000);
    setTimeout(()=> { row.classList.remove('recent-focus','recent-focus-fade'); businessPartnersStore.clearLastVisited(); }, 4200);
    row.scrollIntoView({ behavior:'smooth', block:'center' });
  } catch(e){}
}

function formatDateTime(dateTimeString) {
  // Guard: fehlender oder ungültiger Wert -> "Kein Datum"
  if (!dateTimeString) return 'Kein Datum';
  const date = new Date(dateTimeString);
  if (isNaN(date.getTime())) return 'Kein Datum';
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = String(date.getFullYear()).slice(-2);
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${day}.${month}.${year} ${hours}:${minutes}`;
}

function formatStatusWithDate(statusLabel, dateVal){
  const formatted = formatDateTime(dateVal);
  if(formatted === 'Kein Datum') return statusLabel; // Kein "am" wenn kein Datum
  return `${statusLabel} am ${formatted}`;
}

function formatIndex(index) {
  // Format as 08/10921 style: always two digits, then slash, then total
  // This function now only returns the left part (e.g. 08), the template will add the /total
  return String(index).padStart(2, '0');
}

function formatIndexWithTotal(index, total) {
  // Returns e.g. 08/10.921
  const totalFormatted = Number(total).toLocaleString('de-DE');
  const formatted = String(index).padStart(2, '0') + ' / ' + totalFormatted;
  // console.log('formatIndexWithTotal', { index, total, formatted });
  return formatted;
}

function formatCurrencyEUR(value) {
  const num = Number(value);
  if (!isFinite(num) || isNaN(num)) return '—';
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(num);
}

function formatIconRevenueValue(value) {
  const num = Number(value);
  if (!isFinite(num) || isNaN(num) || num <= 0) return '-';
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(num);
}

function getIconRevenue(bp, channel) {
  if (!bp) return '-';
  const key = String(channel || '').toLowerCase();
  const fallbackLegacy = {
    gs: bp.umsatzGS,
    tb: bp.umsatzTB,
    oe: bp.umsatzOE,
    omp: bp.umsatzOMP,
  };
  const fromSalesValues = bp?.salesValues?.[key];
  const value = fromSalesValues ?? fallbackLegacy[key];
  return formatIconRevenueValue(value);
}

function getRevenue(bp) {
  if (!bp) return undefined;
  return bp.totalRevenue ?? bp.sumRevenue ?? bp.revenueTotal ?? bp.revenue ?? undefined;
}

function getBusinessPartnerLink(businessPartner, index) {
  if (!businessPartner || !businessPartner.gpNo) return null;
  const namedFilter = route.query.namedFilter;
  let extraParam = {};

  if (namedFilter === 'Today:Wiedervorlagen') {
    extraParam = { fromTodayWiedervorlagenList: 'true' };
  } else if (namedFilter === 'Today:VorOrtTermine') {
    extraParam = { fromTodayVorOrtTermineList: 'true' };
  } else {
    extraParam = { fromList: 'true' };
  }

  let listFilterSnapshot = null;
  try {
    listFilterSnapshot = JSON.stringify(userStore.currentSelection?.businessPartnerList?.filter || null);
  } catch (e) {
    listFilterSnapshot = null;
  }

  return {
    name: 'BusinessPartner',
    query: {
      searchMode: 'GPNo',
      search: businessPartner.gpNo,
      position: String(index ?? ''),
      ...(listFilterSnapshot ? { listFilter: listFilterSnapshot } : {}),
      ...extraParam
    }
  };
}

// Prefetch BusinessPartner before navigation to avoid empty detail view (single definition)
const prefetchingBusinessPartner = ref(false);
async function navigateToBusinessPartner(businessPartner, index){
  if(prefetchingBusinessPartner.value) return;
  prefetchingBusinessPartner.value = true;
  try {
    // Keep current list/search context untouched so detail prev/next can navigate within the full list.
    if (businessPartner?.id) {
      userStore.currentSelection.businessPartner.id = businessPartner.id;
    }
    if (businessPartner?.gpNo) {
      userStore.currentSelection.businessPartner.gpNo = String(businessPartner.gpNo);
    }
  } finally {
    prefetchingBusinessPartner.value = false;
  }
  // Remember row for highlight on return; approximate page by index/pageSize
  try {
    const pageSize = Number(userStore.currentSelection.businessPartnerList.pageSize) || 50;
    const page = Math.ceil((index + 1) / pageSize);
    businessPartnersStore.setLastVisited({ ...businessPartner, __page: page });
  } catch(e){}
  const target = getBusinessPartnerLink(businessPartner, index);
  if (!target) return;
  await router.push(target);
}

// function formattedSalesAgentName(name) {
//     return name
//         .toLowerCase() // Konvertiere in Kleinbuchstaben
//         .replace(/ä/g, 'ae') // Ersetze ä durch ae
//         .replace(/ö/g, 'oe') // Ersetze ö durch oe
//         .replace(/ü/g, 'ue') // Ersetze ü durch ue
//         .replace(/ß/g, 'ss') // Ersetze ß durch ss
//         .replace(/\s+/g, '-') // Ersetze Leerzeichen durch -
//         + '.webp'; // Füge die Dateiendung hinzu
//     };

// function salesAgentStatus(idSalesAgent) {
//   const salesAgent = userStore.filterOption?.salesAgents?.find(salesAgent => salesAgent.id === idSalesAgent)
//   const status = incentivesStore.incentive.salesAgents.champions.find(champion => salesAgent.idUser === champion.idUser)?.salesAgentStatusCurrent
//   return status
// }
import InlineSpinner from '@/components/InlineSpinner.vue';

// Hilfsfunktion, um die Trefferzahl aus der Summary zu extrahieren

const totalCountFromSummary = computed(() => {
  const summary = businessPartnersStore.businessPartnerList.summary;
  if (!summary) return '?';
  const match = summary.match(/(\d+) Treffer/);
  return match ? match[1] : '?';
});

// Spaltenkonfiguration für die Listenansicht (Desktop)
const defaultAllColumns = [
  { key: 'salesManager', label: 'Sales Manager', essential: false },
  { key: 'status', label: 'Mein Kontakstatus', essential: true },
  { key: 'name', label: 'Name / Firma', essential: true },
  { key: 'address', label: 'Adresse', essential: false },
  { key: 'gs', label: 'GS', essential: false },
  { key: 'tb', label: 'TB', essential: false },
  { key: 'oe', label: 'OE', essential: false },
  { key: 'omp', label: 'OMP', essential: false },
  { key: 'revenue', label: 'Gesamt', essential: false },
];

const allColumns = ref(defaultAllColumns.map(c => ({ ...c })));
const defaultVisibleColumnKeys = defaultAllColumns.map(c => c.key);
const visibleColumnKeys = ref(defaultVisibleColumnKeys.slice());
const showColumnMenu = ref(false);

// Separate Vorschau-Reihenfolge nur für das Dropdown, damit die Tabelle
// erst beim Loslassen neu sortiert wird.
const columnOrderPreview = ref(allColumns.value.map(c => c.key));

// Benannte Vorlagen für Spalten-Konfiguration (Mehrere Presets)
const columnPresets = ref([]);
const activePresetId = ref(null);
const newPresetName = ref('');

function syncColumnOrderPreviewFromAllColumns() {
  columnOrderPreview.value = allColumns.value.map(c => c.key);
}

// Spaltenliste im Dropdown folgt immer der Vorschau-Reihenfolge
const menuColumns = computed(() => {
  return columnOrderPreview.value
    .map(key => allColumns.value.find(c => c.key === key))
    .filter(Boolean);
});

function isColumnVisible(key) {
  return visibleColumnKeys.value.includes(key);
}

// Persistente Konfiguration für Spalten (Reihenfolge + Sichtbarkeit)
const COLUMN_CONFIG_STORAGE_KEY = 'bpListColumnConfig';
// Bei Erhöhung wird eine gespeicherte (nicht-benannte) Spaltenreihenfolge verworfen und
// auf den aktuellen Standard zurückgesetzt. Verhindert, dass alte/kaputte Reihenfolgen
// (z.B. "Mein Kontaktstatus" ganz am Ende statt an Position 2) unbegrenzt weiterleben.
const COLUMN_CONFIG_VERSION = 2;

function loadColumnConfig() {
  try {
    const raw = window.localStorage?.getItem(COLUMN_CONFIG_STORAGE_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw);
    // Presets aus Storage laden (neues Format)
    const storedPresets = Array.isArray(parsed?.presets) ? parsed.presets : [];
    columnPresets.value = storedPresets;
    activePresetId.value = typeof parsed?.activePresetId === 'string' ? parsed.activePresetId : null;

    // Effektive Reihenfolge/Sichtbarkeit bestimmen: zuerst aktives Preset, sonst altes Format
    let savedOrder = [];
    let savedVisible = [];
    let orderWasOutdated = false;
    const activePreset = columnPresets.value.find(p => p.id === activePresetId.value);
    if (activePreset) {
      savedOrder = Array.isArray(activePreset.order) ? activePreset.order : [];
      savedVisible = Array.isArray(activePreset.visible) ? activePreset.visible : [];
    } else if (parsed?.version === COLUMN_CONFIG_VERSION) {
      savedOrder = Array.isArray(parsed?.order) ? parsed.order : [];
      savedVisible = Array.isArray(parsed?.visible) ? parsed.visible : [];
    } else {
      // Unbekannte/alte Version: Reihenfolge verwerfen (-> Standard), Sichtbarkeit bleibt erhalten.
      savedVisible = Array.isArray(parsed?.visible) ? parsed.visible : [];
      orderWasOutdated = true;
    }

    // Reihenfolge anwenden
    if (savedOrder.length) {
      const reordered = [];
      for (const key of savedOrder) {
        const col = allColumns.value.find(c => c.key === key);
        if (col && !reordered.includes(col)) reordered.push(col);
      }
      // Neue/fehlende Spalten anhängen
      for (const col of allColumns.value) {
        if (!reordered.includes(col)) reordered.push(col);
      }
      allColumns.value = reordered;

      // Dropdown-Vorschau an neue Reihenfolge anpassen
      syncColumnOrderPreviewFromAllColumns();
    }

    // Sichtbarkeit anwenden – essentielle Spalten immer sichtbar lassen
    if (savedVisible.length) {
      const nextVisible = allColumns.value
        .filter(col => col.essential || savedVisible.includes(col.key))
        .map(col => col.key);
      if (nextVisible.length) {
        visibleColumnKeys.value = nextVisible;
      }
    }

    // Korrigierte/versionierte Konfiguration gleich persistieren, damit der Reset nicht bei jedem Laden erneut greift.
    if (orderWasOutdated) {
      saveColumnConfig();
    }
  } catch (e) {
    // Ignorieren, falls JSON/Storage Probleme macht
  }
}

function saveColumnConfig() {
  try {
    const payload = {
      version: COLUMN_CONFIG_VERSION,
      order: allColumns.value.map(c => c.key),
      visible: visibleColumnKeys.value.slice(),
      presets: columnPresets.value,
      activePresetId: activePresetId.value,
    };
    window.localStorage?.setItem(COLUMN_CONFIG_STORAGE_KEY, JSON.stringify(payload));
  } catch (e) {
    // Falls Storage nicht verfügbar ist, einfach still ignorieren
  }
}

function showAllColumns() {
  visibleColumnKeys.value = allColumns.value.map(c => c.key);
}

function showOnlyEssentialColumns() {
  visibleColumnKeys.value = allColumns.value
    .filter(col => col.essential)
    .map(col => col.key);
}

// Dynamische Logik: Wann darf die Namensspalte "weit aufgehen"?
// Wir zählen nur wirklich breite Nachbarspalten (ohne schmale Umsatz-/Statusspalten).
const narrowColumnKeysForName = ['status', 'gs', 'tb', 'oe', 'omp', 'revenue'];

const widePeerCountForName = computed(() => {
  return visibleColumnKeys.value.filter(
    key => key !== 'name' && !narrowColumnKeysForName.includes(key)
  ).length;
});

const shouldNameColumnBeWide = computed(() => widePeerCountForName.value <= 2);

function resetColumnsToDefault() {
  allColumns.value = defaultAllColumns.map(c => ({ ...c }));
  visibleColumnKeys.value = defaultVisibleColumnKeys.slice();
  syncColumnOrderPreviewFromAllColumns();
  activePresetId.value = null;
  // Standard-Reihenfolge auch persistent machen, ohne Presets zu löschen
  saveColumnConfig();
}

// Aktuelle Konfiguration als benannte Vorlage speichern
function saveCurrentAsPreset() {
  const name = (newPresetName.value || '').trim();
  if (!name) return;

  const id = `preset-${Date.now()}`;
  const preset = {
    id,
    name,
    order: allColumns.value.map(c => c.key),
    visible: visibleColumnKeys.value.slice(),
  };

  // Falls es bereits eine Vorlage mit gleichem Namen gibt, wird sie ersetzt
  const withoutSameName = columnPresets.value.filter(p => p.name !== name);
  columnPresets.value = [...withoutSameName, preset];
  activePresetId.value = id;
  newPresetName.value = '';
  saveColumnConfig();
}

// Benannte Vorlage anwenden
function applyPresetById(id) {
  const preset = columnPresets.value.find(p => p.id === id);
  if (!preset) return;

  activePresetId.value = id;

  const orderKeys = Array.isArray(preset.order) ? preset.order : [];
  const visibleKeys = Array.isArray(preset.visible) ? preset.visible : [];

  if (orderKeys.length) {
    const reordered = [];
    for (const key of orderKeys) {
      const col = allColumns.value.find(c => c.key === key);
      if (col && !reordered.includes(col)) reordered.push(col);
    }
    for (const col of allColumns.value) {
      if (!reordered.includes(col)) reordered.push(col);
    }
    allColumns.value = reordered;
  }

  if (visibleKeys.length) {
    const nextVisible = allColumns.value
      .filter(col => col.essential || visibleKeys.includes(col.key))
      .map(col => col.key);
    if (nextVisible.length) {
      visibleColumnKeys.value = nextVisible;
    }
  }

  syncColumnOrderPreviewFromAllColumns();
  saveColumnConfig();
}

// Drag & Drop für Spaltenreihenfolge im Dropdown
const draggingColumnKey = ref(null);
const dragOverColumnKey = ref(null);

function handleColumnDragStart(colKey) {
  draggingColumnKey.value = colKey;
}

function handleColumnDragOver(event, targetColKey) {
  // Erlaubt das Droppen
  event.preventDefault();

  if (!draggingColumnKey.value || draggingColumnKey.value === targetColKey) {
    dragOverColumnKey.value = targetColKey;
    return;
  }

  const fromIndex = columnOrderPreview.value.indexOf(draggingColumnKey.value);
  const toIndex = columnOrderPreview.value.indexOf(targetColKey);
  if (fromIndex === -1 || toIndex === -1 || fromIndex === toIndex) {
    dragOverColumnKey.value = targetColKey;
    return;
  }

  // Nur die Vorschau-Liste im Dropdown live umsortieren, nicht die tatsächliche Tabellenreihenfolge
  const updated = columnOrderPreview.value.slice();
  const [moved] = updated.splice(fromIndex, 1);
  updated.splice(toIndex, 0, moved);
  columnOrderPreview.value = updated;

  dragOverColumnKey.value = targetColKey;
}

function handleColumnDragLeave(_event, targetColKey) {
  // Nur Marker zurücksetzen, wenn wir die aktuelle Zielzeile verlassen
  if (dragOverColumnKey.value === targetColKey) {
    dragOverColumnKey.value = null;
  }
}

function handleColumnDrop() {
  if (!draggingColumnKey.value) {
    dragOverColumnKey.value = null;
    return;
  }

  // Beim Loslassen erst die tatsächliche Tabellenreihenfolge anpassen
  const ordered = [];
  for (const key of columnOrderPreview.value) {
    const col = allColumns.value.find(c => c.key === key);
    if (col && !ordered.includes(col)) ordered.push(col);
  }
  for (const col of allColumns.value) {
    if (!ordered.includes(col)) ordered.push(col);
  }
  allColumns.value = ordered;

  // Vorschauliste ist bereits in dieser Reihenfolge, aber zur Sicherheit synchronisieren
  syncColumnOrderPreviewFromAllColumns();

  // Drag beendet, Marker zurücksetzen
  draggingColumnKey.value = null;
  dragOverColumnKey.value = null;
}

// Beim Laden versuchen, gespeicherte Spaltenkonfiguration wiederherzustellen
loadColumnConfig();
</script>


<template>
  <div class="reminder-wrapper singlepage-wrapper max-width business-partner-list-page">
    <div
      v-if="shouldShowGlobalLoadingOverlay"
      class="container"
    >
        <!-- <div class="loader fullPage" id="loader"></div> -->
        <!-- <div class="loadingDescription">Daten werden geladen</div> -->
        <transition name="fade">
            <div class="list-loading-overlay">
              <div class="spinner-center">
                <InlineSpinner size="lg" />
                <div class="loadingDescription" style="margin-top: 1.2em; font-size: 1.1em; color: #222; font-weight: 600;">Daten werden geladen</div>
              </div>
            </div>
          </transition>
    </div>
    <main id="container-main">
        <preview-header
          title="Potential & BK"
          :to="{ name: 'Dashboard', hash: '#potential-bk' }"
          button-label="Zurück zum Dashboard"
          :disabled="exceededToDos && (exceededToDos.feedbacks || exceededToDos.followUps)"
          :loading-disabled="userStore.currentSelection.businessPartnerList.isLoading"
          arrow="left"
        >
          <template #summary-extra>
            <FilterSummaryChips :alwaysShowText="true" />
          </template>
          <template #below-btn>
            <div class="header-toggle-row">
              <div
                v-if="userStore.currentSelection.businessPartnerList.layout === 'list'"
                class="column-chooser"
              >
                <button
                  type="button"
                  class="column-toggle-btn ph-summary-btn"
                  @click.stop="showColumnMenu = !showColumnMenu"
                >
                  Spalten ein-/ausblenden
                </button>
                <div v-if="showColumnMenu" class="column-menu" @click.stop>
                  <div class="column-menu-title">Spalten ein-/ausblenden</div>
                  <label
                    v-for="col in menuColumns"
                    :key="col.key"
                    class="column-menu-item"
                    :class="{
                      'is-dragging': draggingColumnKey === col.key,
                      'is-drag-target': dragOverColumnKey === col.key
                    }"
                    draggable="true"
                    @dragstart="handleColumnDragStart(col.key)"
                    @dragover="handleColumnDragOver($event, col.key)"
                    @dragleave="handleColumnDragLeave($event, col.key)"
                    @drop.prevent="handleColumnDrop()"
                  >
                    <span class="column-menu-drag-handle" aria-hidden="true">
                      <svg width="12" height="12" viewBox="0 0 20 20" focusable="false">
                        <circle cx="5" cy="5" r="1.4" />
                        <circle cx="5" cy="10" r="1.4" />
                        <circle cx="5" cy="15" r="1.4" />
                        <circle cx="11" cy="5" r="1.4" />
                        <circle cx="11" cy="10" r="1.4" />
                        <circle cx="11" cy="15" r="1.4" />
                      </svg>
                    </span>
                    <input
                      type="checkbox"
                      v-model="visibleColumnKeys"
                      :value="col.key"
                      :disabled="col.essential"
                    />
                    <span>{{ col.label }}</span>
                  </label>
                  <div class="column-menu-actions">
                    <button type="button" class="column-save-btn column-quick-btn" @click="showAllColumns">
                      Alle einblenden
                    </button>
                    <button type="button" class="column-save-btn column-quick-btn" @click="showOnlyEssentialColumns">
                      Nur Pflichtspalten
                    </button>
                    <button type="button" class="column-save-btn" @click="saveColumnConfig">
                      Im Browser speichern
                    </button>
                  </div>
                  <div class="column-presets">
                    <div class="column-presets-header">Vorlagen</div>
                    <div class="column-presets-row">
                      <button
                        type="button"
                        class="column-preset-btn"
                        @click="resetColumnsToDefault"
                      >
                        Standard-Reihenfolge
                      </button>
                    </div>
                    <div v-if="columnPresets.length" class="column-presets-list">
                      <span class="column-presets-label">Gespeicherte:</span>
                      <div class="column-presets-chips">
                        <button
                          v-for="preset in columnPresets"
                          :key="preset.id"
                          type="button"
                          class="column-preset-chip"
                          :class="{ 'is-active': activePresetId === preset.id }"
                          @click="applyPresetById(preset.id)"
                        >
                          {{ preset.name }}
                        </button>
                      </div>
                    </div>
                    <div class="column-presets-save">
                      <input
                        v-model="newPresetName"
                        type="text"
                        class="column-preset-input"
                        placeholder="Neue Vorlage benennen…"
                        @keydown.stop
                      />
                      <button
                        type="button"
                        class="column-preset-save-btn"
                        @click="saveCurrentAsPreset"
                      >
                        Vorlage speichern
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              <button class="collapse-filter-btn ph-summary-btn" @click="userStore.currentSelection.businessPartnerList.filter.show = !userStore.currentSelection.businessPartnerList.filter.show">
                {{ userStore.currentSelection.businessPartnerList.filter.show ? 'Filter ausblenden' : 'Filter einblenden' }}
              </button>
            </div>
          </template>
        </preview-header>
      <div>
        <div class="widget-wrapper potential-bk">
          <!-- <router-link to="/dashboard#potential-bk" :tabindex="userStore.currentSelection.businessPartnerList.isLoading ? -1 : 0" :aria-disabled="userStore.currentSelection.businessPartnerList.isLoading ? 'true' : 'false'" :class="{ 'disabled-link': userStore.currentSelection.businessPartnerList.isLoading }">
            <div class="headline-wrapper">
              <h2>Potential & BK</h2>
              <p>{{ businessPartnersStore.businessPartnerList.summary }}</p>
            </div>
          </router-link> -->
          <div v-if="false" class="toggle-header-wrapper">
            <div class="filterview-toggle">
              <img @click="handleOpenFilter()" class="" src="@/assets/imgs/icons/filter-solid.svg" alt="Filter anzeigen" />
            </div>
            <div class="listview-toggle">
                <img @click="handleToggleLayout()" :class="{'active' : userStore.currentSelection.businessPartnerList.layout === 'list'}" src="@/assets/imgs/icons/list-solid.svg" alt="Listenansicht" />
                <img @click="handleToggleLayout()" :class="{'active' : userStore.currentSelection.businessPartnerList.layout === 'grid'}" src="@/assets/imgs/icons/grip-solid.svg" alt="Gridansicht" />
                <img @click="handleToggleLayout()" :class="{'active' : userStore.currentSelection.businessPartnerList.layout === 'grid'}" src="@/assets/imgs/icons/grip-small-solid.svg" alt="Gridansicht klein" />
            </div>
          </div>
          <div class="container-wrapper scroll-y" :class="{ 'is-reloading': isReloadingList || isLoadingMore }" ref="scrollContainer">
            <!-- Nur ein Listen-Overlay gleichzeitig anzeigen, Priorität: Filter-Reload > Mehr laden > Kunde öffnen -->
            <div
              v-if="isReloadingList"
              class="list-reloading-overlay"
            >
              <div class="list-reloading-inner">
                <InlineSpinner size="lg" />
                <div class="list-reloading-text">Liste wird mit neuem Filter geladen…</div>
              </div>
            </div>
            <div
              v-else-if="isLoadingMore && !shouldShowGlobalLoadingOverlay"
              class="list-reloading-overlay"
            >
              <div class="list-reloading-inner">
                <InlineSpinner size="lg" />
                <div class="list-reloading-text">Weitere Kunden werden geladen</div>
              </div>
            </div>
            <div
              v-else-if="isOpeningBusinessPartner && !shouldShowGlobalLoadingOverlay"
              class="list-reloading-overlay"
            >
              <div class="list-reloading-inner">
                <InlineSpinner size="lg" />
                <div class="list-reloading-text">Kunde wird geöffnet…</div>
              </div>
            </div>
            <div v-if="userStore.selectedMedia === 'mobile' || userStore.currentSelection.businessPartnerList.layout === 'grid'" class="businessPartnerList businessPartnerList--cards" ref="businessPartnerList" style="position:relative;">
              <div :id="businessPartner.gpNo" :data-bpid="businessPartner.id" :class="['card main-content',  {'unseen' : businessPartner.unseen, 'recent-focus': businessPartner.id === businessPartnersStore.lastVisited.id}]" v-for="(businessPartner, index) in businessPartnersStore.businessPartnerList.data" :key="businessPartner.id">
                <span class="highlightBorder"></span>
                <div class="card-header">
                  <div class="card-header-row">
                    <div class="labels">
                      <span class="index-pill">{{ formatIndexWithTotal(index + 1, userStore.currentSelection.businessPartnerList.maxRows || businessPartnersStore.businessPartnerList.data.length) }}</span>
                      <span v-if="businessPartner.status?.id >= 2 && businessPartner.status?.id <= 4" class="status">{{ businessPartner.status?.listAs }}</span>
                      <span v-else-if="businessPartner.status?.id >= 6 && businessPartner.status?.id <= 7" class="status">{{ businessPartner.status?.listAs }}</span>
                      <span v-else-if="businessPartner.status?.id >= 8 && businessPartner.status?.id <= 9" class="status">Beendet: {{ businessPartner.status?.listAs }}</span>
                      <span v-else-if="businessPartner.status?.id === 22" class="status">{{ businessPartner.status?.listAs }}</span>
                      <span v-else-if="businessPartner.status?.id === null" class="status">Unbearbeitet</span>
                      <span v-else class="status">{{ businessPartner.status?.listAs }}</span>
                    </div>
                    <span v-if="businessPartner.salesPitch?.dateFollowUp" class="date-pill">Frist: {{ formatDateTime(businessPartner.salesPitch.dateFollowUp) }}</span>
                  </div>
                  <div class="card-header-row card-header-name-row">
                    <h3 class="bp-name">
                      <a href="#" @click.prevent="navigateToBusinessPartner(businessPartner, index)" v-html="highlightText(businessPartner.name)"></a>
                    </h3>
                  </div>
                  <div class="card-header-row card-header-meta-row">
                    <span class="revenue-pill">{{ formatCurrencyEUR(getRevenue(businessPartner)) }}</span>
                    <span class="gpno">GP-Nr: <span v-html="highlightText(String(businessPartner.gpNo))"></span></span>
                    <CountdownBadge
                      v-if="(businessPartner.dateUnassign || businessPartner.dateUnAssign) && new Date(businessPartner.dateUnassign || businessPartner.dateUnAssign) > new Date()"
                      :date-unassign="businessPartner.dateUnassign || businessPartner.dateUnAssign"
                    />
                  </div>
                  <div class="card-icons">
                    <div class="indicator-wrapper">
                      <div class="umsatz-value revenue-total-value">{{ getIconRevenue(businessPartner, 'gs') }}</div>
                      <img :class="{ 'active': (businessPartner.aboStatus?.includes('GS')) }" src="@/assets/imgs/logos/campaigns/Gelbe-Seiten-App-Icon.svg" alt="Abo in GS" />
                    </div>
                    <div class="indicator-wrapper">
                      <div class="umsatz-value revenue-total-value">{{ getIconRevenue(businessPartner, 'tb') }}</div>
                      <img :class="{ 'active': (businessPartner.aboStatus?.includes('TB')) }" src="@/assets/imgs/logos/campaigns/Das-Telefonbuch-App-Icon.svg" alt="Abo im TB" />
                    </div>
                    <div class="indicator-wrapper">
                      <div class="umsatz-value revenue-total-value">{{ getIconRevenue(businessPartner, 'oe') }}</div>
                      <img :class="{ 'active': (businessPartner.aboStatus?.includes('OE')) }" src="@/assets/imgs/logos/campaigns/Das-Oertliche-App-Icon.svg" alt="Abo im ÖTB" />
                    </div>
                    <div class="indicator-wrapper">
                      <div class="umsatz-value revenue-total-value">{{ getIconRevenue(businessPartner, 'omp') }}</div>
                      <span :class="['omp-icon', { 'active': (businessPartner.aboStatus?.includes('OMP')) }]">OMP</span>
                    </div>
                  </div>
                  <div class="card-sales-manager" v-if="userStore.isManagement || userStore.isSalesTeamManager || userStore.isWinback">
                    <userAvatar
                      :size="48"
                      layout="row"
                      :showSalesAgent="true"
                      :isSalesAgent="!userStore.isManagement && !userStore.isSalesTeamManager && !userStore.isWinback"
                      :isSalesTeam="display === 'salesTeams'"
                      :salesAgentId="businessPartner.idSalesAgent"
                    />
                  </div>
                </div>
                <div class="card-body">
                  <ul>
                    <li v-if="businessPartner.salesPitch?.contact">
                      <template v-if="businessPartner.salesPitch?.contact.gender === 'm'">Herr</template>
                      <template v-else-if="businessPartner.salesPitch?.contact.gender === 'f'">Frau</template>
                      {{ businessPartner.salesPitch?.contact.firstName }} {{ businessPartner.salesPitch?.contact.lastName }}
                    </li>
                    <li v-if="businessPartner.addressList && businessPartner.addressList.length > 0">
                      <a target="_blank" :href="'https://www.google.com/maps/place/' + businessPartner.addressList[0].name.split(',')[0].split(' ')[0] + '+' + businessPartner.addressList[0].name.split(',')[0].split(' ')[1] + ',+' + businessPartner.addressList[0].name.split(',')[1].split(' ')[1] + '+' + businessPartner.addressList[0].name.split(',')[1].split(' ')[2]">
                        <img src="@/assets/imgs/logos/google/Google__G__logo.png" alt="Google" class="google-link-icon" />
                        <span v-html="highlightText(businessPartner.addressList[0].name.split(',')[0] + ', ' + businessPartner.addressList[0].name.split(',')[1])"></span>
                      </a>
                    </li>
                    <li v-if="businessPartner.businessCategoryList && businessPartner.businessCategoryList.length > 0 && businessPartner.businessCategoryList[0].name">
                      <span v-html="highlightText(businessPartner.businessCategoryList[0].name)"></span>
                    </li>
                  </ul>
                </div>
                <div
              v-if="((index+1) % userStore.currentSelection.businessPartnerList.pageSize) === 0"
              :id="`EndOfPage:${ ((index + 1) / userStore.currentSelection.businessPartnerList.pageSize) - 1 }`"
              :class="[
                'page-separator',
                { 'page-separator--new': isNewlyLoadedPage((index + 1) / userStore.currentSelection.businessPartnerList.pageSize) }
              ]"
            >
              <span class="page-pill">
                {{
                  isNewlyLoadedPage((index + 1) / userStore.currentSelection.businessPartnerList.pageSize)
                    ? `Neu geladen · Seite ${ (index + 1) / userStore.currentSelection.businessPartnerList.pageSize }`
                    : `Seite ${ (index + 1) / userStore.currentSelection.businessPartnerList.pageSize }`
                }}
              </span>
            </div>
              </div>
            </div>
            <div v-if="userStore.currentSelection.businessPartnerList.layout === 'list'" class="container" ref="businessPartnerListDesktop" style="position:relative;">
                <table>
                    <thead>
                      <tr>
                        <template v-for="col in allColumns" :key="col.key">
                          <th v-if="col.key === 'salesManager' && isColumnVisible('salesManager')">Sales Manager</th>
                          <th v-if="col.key === 'status' && isColumnVisible('status')" class="status-col-header">Mein Kontaktstatus</th>
                          <!-- <th>Datum</th> -->
                          <th v-if="col.key === 'gs' && isColumnVisible('gs')" class="revenue-col-header">
                            <img src="@/assets/imgs/logos/campaigns/Gelbe-Seiten-App-Icon.svg" alt="GS" style="height:24px;display:block;margin:auto;" />
                          </th>
                          <th v-if="col.key === 'tb' && isColumnVisible('tb')" class="revenue-col-header">
                            <img src="@/assets/imgs/logos/campaigns/Das-Telefonbuch-App-Icon.svg" alt="TB" style="height:24px;display:block;margin:auto;" />
                          </th>
                          <th v-if="col.key === 'oe' && isColumnVisible('oe')" class="revenue-col-header">
                            <img src="@/assets/imgs/logos/campaigns/Das-Oertliche-App-Icon.svg" alt="OE" style="height:24px;display:block;margin:auto;" />
                          </th>
                          <th v-if="col.key === 'omp' && isColumnVisible('omp')" class="revenue-col-header">
                            <span class="omp-header-badge">OMP</span>
                          </th>
                          <th v-if="col.key === 'name' && isColumnVisible('name')">Name / Firma</th>
                          <!-- <th>Entscheider</th> -->
                          <th v-if="col.key === 'address' && isColumnVisible('address')">Adresse</th>
                          <th v-if="col.key === 'revenue' && isColumnVisible('revenue')" class="revenue-total-header">Gesamt</th>
                          <!-- <th>Branche</th> -->
                          <!-- <th>GP Nr.</th> -->
                        </template>
                      </tr>
                    </thead>
                    <tbody>
                      <template
                        v-for="(businessPartner, index) in businessPartnersStore.businessPartnerList.data"
                        :key="businessPartner.id"
                      >
                        <!-- Seitentrenner immer VOR dem ersten Datensatz der Seite -->
                        <tr
                          v-if="index === 0 || (index % userStore.currentSelection.businessPartnerList.pageSize) === 0"
                          :id="`StartOfPage:${ Math.floor(index / userStore.currentSelection.businessPartnerList.pageSize) }`"
                          class="page-separator-row"
                        >
                          <td :colspan="visibleColumnKeys.length">
                            <div
                              :class="[
                                'page-separator',
                                { 'page-separator--new': isNewlyLoadedPage(Math.floor(index / userStore.currentSelection.businessPartnerList.pageSize) + 1) }
                              ]"
                            >
                              <span class="page-pill">
                                {{
                                  isNewlyLoadedPage(Math.floor(index / userStore.currentSelection.businessPartnerList.pageSize) + 1)
                                    ? `Neu geladen · Seite ${ Math.floor(index / userStore.currentSelection.businessPartnerList.pageSize) + 1 }`
                                    : `Seite ${ Math.floor(index / userStore.currentSelection.businessPartnerList.pageSize) + 1 }`
                                }}
                              </span>
                            </div>
                          </td>
                        </tr>
                        <tr
                          :id="businessPartner.gpNo"
                          :data-bpid="businessPartner.id"
                          :class="['bp-row', {
                            unseen: businessPartner.unseen,
                            'recent-focus': businessPartner.id === businessPartnersStore.lastVisited.id,
                            'multi-sm': getSalesManagersWithStatus(businessPartner).length > 1
                          }]"
                        >
                          <template v-for="col in allColumns" :key="col.key">
                          <template v-if="col.key === 'salesManager' && isColumnVisible('salesManager')">
                            <td class="sales-manager-cell">
                              <span class="highlightBorder"></span>
                              <div
                                v-if="getSalesManagersWithStatus(businessPartner).length"
                                class="sales-manager-list"
                                :class="{
                                  'sales-manager-list--has-dimmed': userStore.currentSelection.scope === 'salesAgent' && getSalesManagersWithStatus(businessPartner).length > 1
                                }"
                              >
                                <div
                v-for="sm in getSalesManagersWithStatus(businessPartner)"
                :key="sm.idSalesAgent || sm.idUser"
                :class="[
                  'sales-manager-row',
                  {
                    'sales-manager-row--dimmed':
                      userStore.currentSelection.scope === 'salesAgent'
                      && sm.idSalesAgent
                      && sm.idSalesAgent !== userStore.currentSelection.salesAgent?.id
                  }
                ]"
              >
                                  <userAvatar
                                    :size="32"
                                    layout="row"
                                    :showSalesAgent="true"
                                    :isSalesAgent="!userStore.isManagement && !userStore.isSalesTeamManager && !userStore.isWinback"
                                    :isSalesTeam="display === 'salesTeams'"
                                    :salesAgentId="sm.idSalesAgent"
                                  />
                                  <!-- <div class="sales-manager-text">
                                    <span class="sales-manager-name">{{ sm.name }}</span>
                                    <span class="sales-manager-status">{{ sm.statusLabel }}</span>
                                  </div> -->
                                </div>
                              </div>
                              <span v-else class="sales-manager-empty">—</span>
                            </td>
                          </template>

                          <template v-if="col.key === 'status' && isColumnVisible('status')">
                            <td :id="index" class="status-col-cell">
                              <span class="highlightBorder"></span>
                              <div class="labels">
                                <div class="rowTop">
                                  <span :class="['status']" :id="'_' + businessPartner.status?.id">{{ formatDateTime(businessPartner.salesPitch.dateCreate) }}</span>
                                  <span :class="['status index']" :id="'_' + businessPartner.status?.id">{{ formatIndexWithTotal(index + 1, userStore.currentSelection.businessPartnerList.maxRows || businessPartnersStore.businessPartnerList.data.length) }}</span>
                                </div>
                                <div class="rowBottom">
                                  <template v-if="(businessPartner.dateUnassign || businessPartner.dateUnAssign) && new Date(businessPartner.dateUnassign || businessPartner.dateUnAssign) > new Date()">
                                    <CountdownBadge
                                      :date-unassign="businessPartner.dateUnassign || businessPartner.dateUnAssign"
                                    />
                                  </template>
                                  <template v-else>
                                    <span v-if="businessPartner.status?.id >= 2 && businessPartner.status?.id <= 4" :class="['status']" :id="'_' + businessPartner.status?.id">{{ businessPartner.status?.listAs }} {{ formatDateTime(businessPartner.salesPitch.dateFollowUp) }}</span>
                                    <span v-else-if="businessPartner.status?.id >= 6 && businessPartner.status?.id <= 7" :class="['status']" :id="'_' + businessPartner.status?.id">{{ businessPartner.status?.listAs }} {{ formatDateTime(businessPartner.salesPitch.dateFollowUp) }}</span>
                                    <span v-else-if="businessPartner.status?.id >= 8 && businessPartner.status?.id <= 9" :class="['status']" :id="'_' + businessPartner.status?.id">Beendet: {{ businessPartner.status?.listAs }}</span>
                                    <span v-else-if="businessPartner.status?.id === 22" :class="['status']" :id="'_' + businessPartner.status?.id">{{ businessPartner.status?.listAs }}</span>
                                    <span v-else-if="businessPartner.status?.id === null" :class="['status']" :id="'_' + businessPartner.status?.id">Unbearbeitet</span>
                                    <span v-else :class="['status']" :id="'_' + businessPartner.status?.id">{{ businessPartner.status?.listAs }}</span>
                                  </template>
                                </div>
                              </div>
                            </td>
                          </template>
                          
                          <!-- <td>01.08.2024</td> -->
                          <template v-if="col.key === 'gs' && isColumnVisible('gs')">
                            <td class="revenue-col-cell">
                              <span class="highlightBorder"></span>
                              <div class="productsIcon-wrapper">
                                <div class="indicator-wrapper">
                                  <img :class="{ 'active': (businessPartner.aboStatus?.includes('GS')) }" src="@/assets/imgs/logos/campaigns/Gelbe-Seiten-App-Icon.svg" alt="Abo in GS" />
                                  <div class="umsatz-value revenue-total-value">{{ getIconRevenue(businessPartner, 'gs') }}</div>
                                </div>
                              </div>
                            </td>
                          </template>

                          <template v-if="col.key === 'tb' && isColumnVisible('tb')">
                            <td class="revenue-col-cell">
                              <span class="highlightBorder"></span>
                              <div class="productsIcon-wrapper">
                                <div class="indicator-wrapper">
                                  <img :class="{ 'active': (businessPartner.aboStatus?.includes('TB')) }" src="@/assets/imgs/logos/campaigns/Das-Telefonbuch-App-Icon.svg" alt="Abo im TB" />
                                  <div class="umsatz-value revenue-total-value">{{ getIconRevenue(businessPartner, 'tb') }}</div>
                                </div>
                              </div>
                            </td>
                          </template>

                          <template v-if="col.key === 'oe' && isColumnVisible('oe')">
                            <td class="revenue-col-cell">
                              <span class="highlightBorder"></span>
                              <div class="productsIcon-wrapper">
                                <div class="indicator-wrapper">
                                  <img :class="{ 'active': (businessPartner.aboStatus?.includes('OE')) }" src="@/assets/imgs/logos/campaigns/Das-Oertliche-App-Icon.svg" alt="Abo im ÖTB" />
                                  <div class="umsatz-value revenue-total-value">{{ getIconRevenue(businessPartner, 'oe') }}</div>
                                </div>
                              </div>
                            </td>
                          </template>

                          <template v-if="col.key === 'omp' && isColumnVisible('omp')">
                            <td class="revenue-col-cell">
                              <span class="highlightBorder"></span>
                              <div class="productsIcon-wrapper">
                                <div class="indicator-wrapper">
                                  <span :class="['omp-icon', { 'active': (businessPartner.aboStatus?.includes('OMP')) }]">OMP</span>
                                  <div class="umsatz-value revenue-total-value">{{ getIconRevenue(businessPartner, 'omp') }}</div>
                                </div>
                              </div>
                            </td>
                          </template>

                          <template v-if="col.key === 'name' && isColumnVisible('name')">
                            <td :class="['name-col-cell', { 'name-col-cell--wide': shouldNameColumnBeWide }]">
                              <span class="highlightBorder"></span>
                              <div class="infoBusinessPartner">               
                                <div class="unseenDotWrapper">
                                  <p class="businessPartner">
                                    <a href="#" @click.prevent="navigateToBusinessPartner(businessPartner, index)" v-html="highlightText(businessPartner.name)"></a>
                                  </p>
                                </div><p>
                                  <span v-html="highlightText(String(`(GP-Nr.: ${businessPartner.gpNo}) `))"></span> 
                                    <span class="businessPartnerContact" v-if="businessPartner.salesPitch?.contact"> 
                                    <template v-if="businessPartner.salesPitch?.contact.gender === 'm'">Herr</template>
                                    <template v-else-if="businessPartner.salesPitch?.contact.gender === 'f'">Frau</template> 
                                    <span v-html="highlightText(` ${businessPartner.salesPitch?.contact.firstName} ${businessPartner.salesPitch?.contact.lastName}`)"></span></span>
                                </p>
                              </div>
                            </td>
                          </template>

                          <template v-if="col.key === 'address' && isColumnVisible('address')">
                            <td>
                              <span class="highlightBorder"></span>
                              <div class="infoAdress">
                                <p v-if="businessPartner.addressList && businessPartner.addressList[0].name.split(',')[0].split(' ')[0]">
                                  <a target="_blank" :href="'https://www.google.com/maps/place/' + 
                                    businessPartner.addressList[0].name.split(',')[0].split(' ')[0] + 
                                    '+' +
                                    businessPartner.addressList[0].name.split(',')[0].split(' ')[1] + 
                                    ',+' +
                                    businessPartner.addressList[0].name.split(',')[1].split(' ')[1] + 
                                    '+' +
                                    businessPartner.addressList[0].name.split(',')[1].split(' ')[2]"
                                  >
                                    <img src="@/assets/imgs/logos/google/Google__G__logo.png" alt="Google" class="google-link-icon" />
                                    <span v-html="highlightText(businessPartner.addressList[0].name.split(',')[0] + ', ' + businessPartner.addressList[0].name.split(',')[1])"></span>
                                  </a>
                                </p>
                                <p class="disabled" v-else>Keine Adresse bekannt!</p>
                                <p class="businessCategoryList" v-if="businessPartner.businessCategoryList && businessPartner.businessCategoryList.length > 0 && businessPartner.businessCategoryList[0].name" v-html="highlightText(businessPartner.businessCategoryList[0].name)"></p>
                                <p class="businessCategoryList disabled" v-else>Keine Branche eingetragen!</p>
                              </div>
                            </td>
                          </template>

                          <template v-if="col.key === 'revenue' && isColumnVisible('revenue')">
                            <td class="revenue-total-cell">
                              <span class="highlightBorder"></span>
                              <span class="revenue-total-value">{{ formatCurrencyEUR(getRevenue(businessPartner)) }}</span>
                            </td>
                          </template>
                        </template>
                        </tr>
                      </template>
                        <!-- Weitere Zeilen können hier hinzugefügt werden -->
                    </tbody>
                </table>              
            </div>
          </div>
          <div class="load-more-row" v-if="userStore.currentSelection.businessPartnerList.maxRows > businessPartnersStore.businessPartnerList.data.length">
            <button 
              class="load-btn" href="#" 
              :disabled="userStore.currentSelection.businessPartnerList.isLoading || isLoadingMore" 
              @click.prevent="requestNewPage()">
              <InlineSpinner v-if="isLoadingMore" size="sm" />
              <span v-else>Mehr laden</span>
            </button>
            <div class="pagination-info">
              <span>{{ businessPartnersStore.businessPartnerList.data.length }} / {{ (userStore.currentSelection.businessPartnerList.maxRows || totalCountFromSummary).toLocaleString('de-DE') }} Kunden geladen</span>
            </div>
          </div>
          <div class='wave-wrapper'>
            <div class='wave -one'></div>
            <div class='wave -two'></div>
            <div class='wave -three'></div>
          </div>
        </div>

              <!-- <div class="btn filter accent" @click="handleOpenFilter()">Filter setzen</div> -->
              <BusinessPartnerFilter v-if="userStore.selectedMedia === 'desktop' && userStore.currentSelection.businessPartnerList.filter.show"></BusinessPartnerFilter>

      </div>
    </main>
    <div v-if="userStore.currentSelection.businessPartnerList.filter.show && userStore.selectedMedia === 'mobile'" class="mobile-filter-overlay">
      <button class="mobile-filter-close" @click="userStore.currentSelection.businessPartnerList.filter.show = false">✕</button>
      <BusinessPartnerFilter />
    </div>
    <reminder></reminder>
  </div>
</template>
<style lang="scss">
/* Mobile Filter Overlay */
.mobile-filter-overlay {
  position: fixed;
  top: 75px;
  left: 0;
  width: 100vw;
  height: calc(100vh - 75px);
  background: #fff;
  z-index: 9999;
  overflow-y: auto;
  box-shadow: 0 0 0 100vw rgba(0,0,0,0.25);
  display: flex;
  flex-direction: column;
  padding: 0;
  animation: fadeInOverlay 0.18s;
  .filter-wrapper {
    overflow-y: auto;
  }
}
.mobile-filter-close {
  position: absolute;
  top: 1.1rem;
  right: 1.1rem;
  background: #f1f5f9;
  border: none;
  border-radius: 50%;
  width: 2.5rem;
  height: 2.5rem;
  font-size: 1.7rem;
  color: #334155;
  z-index: 10001;
  box-shadow: 0 2px 8px 0 rgba(100,116,139,0.13);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.18s;
}
.mobile-filter-close:hover {
  background: #e2e8f0;
}
@keyframes fadeInOverlay {
  from { opacity: 0; }
  to { opacity: 1; }
}
      .ph-summary-row {
        display: flex;
        align-items: center;
        gap: 1.2rem;
        margin: 0.5rem 0 1.2rem 0;
      }
      .ph-summary {
        font-size: 1.1em;
        font-weight: 500;
        color: #223;
      }
      .ph-summary-btn {
        font-size: .6rem;
        font-weight: 700;
        text-decoration: none;
        background: #e7f1fa;
        border: 1px solid #86a8c5;
        padding: .4rem .65rem;
        border-radius: 8px;
        color: #13334c;
        display: inline-flex;
        gap: .35rem;
        align-items: center;
        letter-spacing: .05em;
        transition: .18s background, .18s color, .18s border-color, .18s box-shadow;
        position: relative;
        cursor: pointer;
        box-shadow: 0 1px 0 rgba(19, 51, 76, .15);
      }
      .ph-summary-btn:hover {
        background: #d6e8f8;
        border-color: #5e8db3;
        color: #0f2a40;
      }
      .theme-dark .ph-summary-btn,
      .theme--dark .ph-summary-btn {
        background: #24445b;
        border-color: #3a6b8a;
        color: #ecf7ff;
      }
      .theme-dark .ph-summary-btn:hover,
      .theme--dark .ph-summary-btn:hover {
        background: #2d5571;
        border-color: #4a84ab;
      }
.back-btn { font-size:.6rem; font-weight:600; text-decoration:none; background:#fff; border:1px solid #e2e8f0; padding:.4rem .65rem; border-radius:8px; color:#334155; display:inline-flex; gap:.35rem; align-items:center; letter-spacing:.05em; }
/* Overlay for loading spinner on list container */
.list-container-wrapper {
  position: relative;
}
.list-loading-overlay {
  position: fixed;
  inset: 0;
  background: rgba(255, 255, 255, 0.7);
  z-index: 2500;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  pointer-events: all;
}
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s;
}
.fade-enter-from, .fade-leave-to {
  opacity: 0;
}
.back-btn:hover { background:#f1f5f9; }
.theme-dark .back-btn { background:#1e293b; border-color:#334155; color:#e2e8f0; }
.theme-dark .back-btn:hover { background:#334155; }

.umsatz-value {
  font-size: 13px;
  font-weight: 500;
  line-height: 1.1;
  text-align: center;
  margin-bottom: 2px;
  min-height: 14px;
  white-space: nowrap;
}
.carousel__item {
  min-height: 200px;
  width: 100%;
  background-color: var(--vc-clr-primary);
  color: var(--vc-clr-white);
  font-size: 20px;
  border-radius: 8px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.carousel__slide {
  padding: 10px;
}

.carousel__prev,
.carousel__next {
  box-sizing: content-box;
  border: 5px solid white;
}

/* Override isolation for this page so overlays/dropdowns can appear above the list */
.business-partner-list-page main > div {
  isolation: auto !important;
}

/* Ensure header (and its dropdown) is above list & filter */
.business-partner-list-page main > div > .preview-header {
  position: relative;
  z-index: 2000;
}
/* Vollflächiger Overlay beim Neuladen der Liste über den Filter */
.container-wrapper.scroll-y {
  position: relative;
}

/* BusinessPartnerList: use natural page scroll and hide fixed-height constraints.
   potentialSingle.scss's max-height/overflow-y:auto on .widget-wrapper.potential-bk
   is meant for the small Dashboard preview widget (PreviewBusinessPartnerList.vue),
   which shares the same class names — without this reset it also clips this full
   list page to a tiny internally-scrolling box on mobile, cutting cards off mid-row. */
.business-partner-list-page {
  margin-bottom: 0;
}

.business-partner-list-page .widget-wrapper .container-wrapper.scroll-y {
  // overflow-y: visible !important;
}

.singlepage-wrapper .widget-wrapper .container-wrapper.scroll-y.is-reloading {
  /* Verhindert Scrollen/Überlaufen der Tabelle, solange ein Lade-Overlay aktiv ist */
  overflow: hidden;
  overflow-y: hidden;
  overflow-x: hidden;
  table {
    overflow: hidden;
  }
}

.list-reloading-overlay {
  /* Overlay innerhalb des List-Containers, optisch "in der Box" */
  position: fixed;
  inset: 0;
  z-index: 1500;
  background: rgba(255, 255, 255, 0.78);
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: all;
}

.list-reloading-inner {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.85rem;
  padding: 1.2rem 1.8rem;
  border-radius: 1rem;
  background: rgba(255, 255, 255, 0.98);
  box-shadow: 0 12px 30px rgba(15, 23, 42, 0.18);
  border: 1px solid rgba(148, 163, 184, 0.6);
}

.list-reloading-text {
  font-size: 0.9rem;
  font-weight: 600;
  color: #111827;
}

/* Header buttons (Filter + Spalten) in einer Reihe */
.header-toggle-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  position: relative;
  z-index: 50;
}

/* Column chooser for desktop list view */
.column-chooser {
  margin-left: auto;
  position: relative;
  z-index: 60;
}

.column-toggle-btn {
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
}

.column-toggle-btn:hover {
  /* Hover styling comes from .ph-summary-btn to keep both buttons visually identical */
}

.column-menu {
  position: absolute;
  right: 0;
  margin-top: 0.35rem;
  min-width: 320px;
  max-width: 420px;
  padding: 0.7rem 0.75rem;
  border-radius: 0.75rem;
  background: #ffffff;
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.18);
  border: 1px solid #e2e8f0;
  z-index: 1000;
}

/* Schlankere Umsatzspalten (GS, TB, OE, OMP) in der Tabelle */
.revenue-col-header {
  text-align: center;
  padding-left: 4px !important;
  padding-right: 4px !important;
  width: 86px;
}

.revenue-col-cell {
  padding-left: 0 !important;
  padding-right: 0 !important;
  vertical-align: bottom;
  padding-bottom: 8px !important;
}

.revenue-col-cell .productsIcon-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 4px;
}

.revenue-col-cell .indicator-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  width: 42px;
  min-height: 58px;
}

.revenue-col-cell .indicator-wrapper img,
.revenue-col-cell .indicator-wrapper .omp-icon {
  width: 32px;
  height: 32px;
  border-radius: 6px;
  object-fit: contain;
  background: #f9fafb;
}

.revenue-col-cell .indicator-wrapper .omp-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: 700;
  line-height: 1;
  color: #475569;
  text-transform: uppercase;
}

.revenue-col-cell .indicator-wrapper img.active,
.revenue-col-cell .indicator-wrapper .omp-icon.active {
  background: #fffbe6;
}

.omp-header-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 6px;
  background: #f9fafb;
  color: #475569;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  margin: 0 auto;
}

.revenue-total-header {
  text-align: right;
  padding-right: 12px !important;
  white-space: nowrap;
}

.revenue-total-cell {
  text-align: right;
  padding-right: 12px !important;
  white-space: nowrap;
  vertical-align: bottom;
  padding-bottom: 8px !important;
}

.revenue-total-value {
  display: inline-block;
  font-weight: 600;
}

.column-menu-title {
  font-size: 0.7rem;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 0.4rem;
}

.column-menu-item {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.7rem;
  color: #374151;
  padding: 0.12rem 0;
  cursor: grab;
}

.column-menu-item input[type='checkbox'] {
  width: 0.85rem;
  height: 0.85rem;
}

.column-menu-item:active {
  cursor: grabbing;
}

/* Deutliche visuelle Hervorhebung beim Drag & Drop */
.column-menu-item.is-dragging {
  opacity: 0.6;
}

.column-menu-item.is-drag-target {
  background: rgba(var(--sellwerk-color-main-accent-rgb,255,136,0), 0.08);
  border-radius: 6px;
  box-shadow: 0 0 0 1px rgba(var(--sellwerk-color-main-accent-rgb,255,136,0), 0.45);
}

.column-menu-drag-handle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 0.9rem;
  color: #9ca3af;
}

.column-menu-drag-handle svg {
  fill: currentColor;
}

.column-menu-actions {
  margin-top: 0.45rem;
  padding-top: 0.35rem;
  border-top: 1px solid #e5e7eb;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.35rem;
  justify-items: center;
}

.column-save-btn {
  display: block;
  font-size: 0.7rem;
  font-weight: 600;
  padding: 0.22rem 0.7rem;
  border-radius: 999px;
  border: 1px solid #e5e7eb;
  background: #f9fafb;
  color: #111827;
  cursor: pointer;
  text-align: center;
  width: 100%;
}

/* Speichern-Button im Spaltenmenü über volle Breite */
.column-menu-actions .column-save-btn:not(.column-quick-btn) {
  grid-column: 1 / -1;
  width: 100%;
}

.column-quick-btn {
  background: #f9fafb;
  border-color: #e5e7eb;
  color: #111827;
}

.column-quick-btn:hover {
  background: #e5e7eb;
}

.column-save-btn:hover {
  background: #e5e7eb;
}

/* Vorlagen-Bereich im Spaltenmenü */
.column-presets {
  margin-top: 0.55rem;
  padding-top: 0.45rem;
  border-top: 1px solid #e5e7eb;
}

.column-presets-header {
  font-size: 0.68rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #6b7280;
  margin-bottom: 0.3rem;
}

.column-presets-row {
  margin-bottom: 0.35rem;
}

.column-preset-btn {
  font-size: 0.7rem;
  font-weight: 600;
  padding: 0.22rem 0.6rem;
  border-radius: 999px;
  border: 1px dashed rgba(var(--sellwerk-color-main-accent-rgb,255,136,0), 0.5);
  background: rgba(var(--sellwerk-color-main-accent-rgb,255,136,0), 0.08);
  color: var(--sellwerk-color-main-accent);
  cursor: pointer;
}

.column-preset-btn:hover {
  background: rgba(var(--sellwerk-color-main-accent-rgb,255,136,0), 0.16);
}

.column-presets-list {
  margin-bottom: 0.4rem;
}

.column-presets-label {
  display: block;
  font-size: 0.65rem;
  color: #6b7280;
  margin-bottom: 0.18rem;
}

.column-presets-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
}

.column-preset-chip {
  font-size: 0.68rem;
  padding: 0.14rem 0.55rem;
  border-radius: 999px;
  border: 1px solid #e5e7eb;
  background: #f9fafb;
  color: #374151;
  cursor: pointer;
}

.column-preset-chip.is-active {
  background: var(--sellwerk-color-main-accent);
  border-color: var(--sellwerk-color-main-accent);
  color: #ffffff;
}

.column-presets-save {
  display: flex;
  gap: 0.35rem;
  align-items: center;
}

.column-preset-input {
  flex: 1;
  min-width: 0;
  font-size: 0.7rem;
  padding: 0.18rem 0.4rem;
  border-radius: 6px;
  border: 1px solid #d1d5db;
}

.column-preset-input:focus {
  outline: none;
  border-color: var(--sellwerk-color-main-accent);
  box-shadow: 0 0 0 1px rgba(var(--sellwerk-color-main-accent-rgb,255,136,0), 0.35);
}

.column-preset-save-btn {
  font-size: 0.7rem;
  font-weight: 600;
  padding: 0.22rem 0.6rem;
  border-radius: 999px;
  border: 1px solid var(--sellwerk-color-main-accent);
  background: rgba(var(--sellwerk-color-main-accent-rgb,255,136,0), 0.06);
  color: var(--sellwerk-color-main-accent);
  cursor: pointer;
  white-space: nowrap;
}

.column-preset-save-btn:hover {
  background: rgba(var(--sellwerk-color-main-accent-rgb,255,136,0), 0.14);
}
</style>
<style scoped>
.card-header-name-row {
  width: 100%;
  margin-top: 0.2em;
}
.bp-name {
  width: 100%;
  font-size: 1.22em;
  font-weight: 700;
  color: #223;
  margin: 0;
  line-height: 1.25;
  overflow: hidden;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  word-break: break-word;
}
.card-header-meta-row {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  row-gap: 0.35rem;
  margin-top: 0.15em;
}
/* Modern Card Styles for Grid Layout */
/* Modern Card Styles for Grid Layout - Advanced */
.businessPartnerList--cards {
  display: flex;
  flex-wrap: wrap;
  gap: 2rem;
  justify-content: flex-start;
}
.card.main-content {
  background: #fff;
  border-radius: 18px;
  box-shadow: 0 4px 24px rgba(34, 41, 47, 0.10), 0 1.5px 4px rgba(34,41,47,0.07);
  border: 1px solid #e5e7eb;
  width: 100%;
  max-width: 420px;
  margin: 0 auto 0 auto;
  display: flex;
  flex-direction: column;
  transition: box-shadow 0.2s, border-color 0.2s;
  position: relative;
  overflow: hidden;
}
.card.main-content:hover {
  box-shadow: 0 8px 32px rgba(34,41,47,0.16);
  border-color: #cbd5e1;
}
.card-header {
  padding: 1.2rem 1.4rem 0.7rem 1.4rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  border-bottom: 1px solid #f1f5f9;
}
.card-header-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.6rem 1.2rem;
}
.labels {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  font-size: 1em;
  color: #475569;
  font-weight: 500;
}
.date-pill {
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
  flex-shrink: 0;
  background: #eef2ff;
  color: #4338ca;
  border-radius: 999px;
  padding: 0.18em 0.7em;
  font-size: 0.85em;
  font-weight: 600;
}
.status {
  background: #f1f5f9;
  color: #475569;
  border-radius: 6px;
  padding: 0.18em 0.7em;
  font-size: 0.98em;
  font-weight: 500;
}
/* Index Pill: links, kleiner, dezenter */
.index-pill {
  background: #f3f4f6;
  color: #64748b;
  border-radius: 999px;
  padding: 0.12em 0.5em;
  font-size: 0.89em;
  font-weight: 500;
  letter-spacing: 0.03em;
  margin-right: 0.3em;
}
/* Umsatz Pill: dezenter, kleiner, heller Orange */
.revenue-pill {
  background: linear-gradient(90deg, #ffe29a 0%, #ffd700 100%);
  color: #b45309;
  border-radius: 999px;
  padding: 0.12em 0.7em;
  font-size: 0.98em;
  font-weight: 600;
  box-shadow: none;
  border: 1px solid #ffe29a;
}

.revenue-pill {
  background: linear-gradient(90deg, #ff8800 0%, #ffd700 100%);
  color: #fff;
  border-radius: 999px;
  padding: 0.18em 0.9em;
  font-size: 1.08em;
  font-weight: 700;
  box-shadow: 0 1px 4px rgba(255,136,0,0.08);
  border: none;
}
.gpno {
  font-size: 0.85em;
  color: #64748b;
  font-weight: 500;
  background: #f8fafc;
  border: 1px solid #eef1f5;
  border-radius: 999px;
  padding: 0.18em 0.7em;
  white-space: nowrap;
}
.card-icons {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.4rem;
  padding-top: 0.6rem;
  border-top: 1px solid #f1f5f9;
}
.card-icons .umsatz-value {
  color: #94a3b8;
}
.card-icons .indicator-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.42rem;
  width: 72px;
}
.card-icons img,
.card-icons .omp-icon {
  height: 36px;
  width: 36px;
  border-radius: 6px;
  background: #f9fafb;
  object-fit: contain;
  transition: filter 0.2s;
  box-shadow: none;
}
.card-icons img.active,
.card-icons .omp-icon.active {
  background: #fffbe6;
}
.card-sales-manager {
  margin-top: 0.3rem;
  display: flex;
  align-items: center;
  gap: 0.7rem;
}
.card-body {
  padding: 0.9rem 1.4rem 1.2rem 1.4rem;
  display: flex;
  flex-direction: column;
}
.card-body ul {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}
.card-body li {
  font-size: 1em;
  color: #334155;
  display: flex;
  align-items: center;
  gap: 0.4rem;
}
.card-body li span {
  font-weight: 500;
}
.card-body li a {
  color: #2563eb;
  text-decoration: underline;
  font-size: 0.98em;
}
.card-body li .disabled {
  color: #cbd5e1;
}
/* .highlightBorder {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 4px;
  background: linear-gradient(90deg, #ff8800 0%, #ffd700 100%);
  opacity: 0.12;
  border-radius: 18px 18px 0 0;
} */
@media (max-width: 600px) {
  .businessPartnerList--cards {
    gap: 1.2rem;
  }
  .card.main-content {
    max-width: 98vw;
    margin: 0 auto 0 auto;
    padding: 0;
  }
  .card-header, .card-body {
    padding-left: 0.7rem;
    padding-right: 0.7rem;
  }
}
/* Use project accent color for highlighted matches; v-html needs deep selector */
:deep(mark) { background: rgba(var(--sellwerk-color-main-accent-rgb,255,136,0),.5); padding: 0 .04em; border-radius: .15em; }
</style>
<style scoped>
/* Use project accent color for highlighted matches instead of default yellow */
mark { background: rgba(var(--sellwerk-color-main-accent-rgb,255,136,0),.5); padding: 0 .04em; border-radius: .15em; }
/* Page separators */
.page-separator { position: relative; display:flex; justify-content:center; align-items:center; margin: 10px 0; }
.page-separator::before { content:""; position:absolute; left:0; right:0; height:1px; background: var(--sellwerk-border-muted, #2d3944); }
.page-separator .page-pill { position:relative; z-index:1; background:#1e252c; color:#cbd5e1; border:1px solid #2d3944; border-radius:999px; padding:.2rem .6rem; font-size:.65rem; letter-spacing:.3px; }
.theme--light .page-separator::before { background:#d5dde5; }
.theme--light .page-separator .page-pill { background:#ffffff; color:#475569; border-color:#d5dde5; }
.page-separator-row td { padding: .35rem 0; }

/* Markierung für neu geladene Seiten (nach Klick auf "Mehr laden") */
.page-separator--new::before {
  background: var(--sellwerk-border-muted, #2d3944);
  height: 2px;
}

.page-separator--new .page-pill {
  background: rgba(var(--sellwerk-color-main-accent-rgb,255,136,0), 0.10);
  color: var(--sellwerk-color-main-accent, #ff8800);
  border-color: rgba(var(--sellwerk-color-main-accent-rgb,255,136,0), 0.55);
  font-weight: 600;
}

/* Recent focus highlight for returned row/card */
@keyframes recentFocusPulse {
  0% { background: rgba(255, 200, 0, .12); box-shadow: 0 0 0 rgba(255,200,0,0); filter: brightness(1.03); }
  50% { background: rgba(255, 200, 0, .20); box-shadow: 0 0 0 3px rgba(255,200,0,.15); filter: brightness(1.06); }
  100% { background: transparent; box-shadow: 0 0 0 rgba(255,200,0,0); filter: brightness(1.0); }
}
/* Grid cards */
.container.main-content.recent-focus { animation: recentFocusPulse 2.4s ease-in-out 1; }
.container.main-content.recent-focus.recent-focus-fade { transition: background 1.2s ease, box-shadow 1.2s ease, filter 1.2s ease; }
/* Table rows */
table tbody tr.recent-focus { animation: recentFocusPulse 2.4s ease-in-out 1; }
.bp-row {
  position: relative;
}
.bp-row td {
  position: relative;
  z-index: 0;
  background: rgba(15, 23, 42, 0.9);
  border-top: 1px solid #111827;
  border-bottom: 1px solid #111827;
}
.bp-row td:not(:first-child) .highlightBorder {
  display: none;
}
.bp-row td:first-child {
  border-left: 1px solid #111827;
  border-top-left-radius: 11px;
  border-bottom-left-radius: 11px;
}
.bp-row td:last-child {
  border-right: 1px solid #111827;
  border-top-right-radius: 11px;
  border-bottom-right-radius: 11px;
}
.theme--light .bp-row td {
  background: #f8fafc;
  border-top-color: #e2e8f0;
  border-bottom-color: #e2e8f0;
}
.theme--light .bp-row td:first-child {
  border-left-color: #e2e8f0;
}
.theme--light .bp-row td:last-child {
  border-right-color: #e2e8f0;
}
.bp-row.unseen td {
  border-color: rgba(199, 20, 24, 1);
  /* box-shadow: 0 0 0 1px rgba(249, 115, 22, 0.25); */
}
.bp-row.multi-sm td {
  /* background: linear-gradient(90deg, rgba(56, 189, 248, 0.12), rgba(129, 140, 248, 0.12)); */
}
.theme--light .bp-row.multi-sm td {
  /* background: linear-gradient(90deg, rgba(56, 189, 248, 0.10), rgba(129, 140, 248, 0.10)); */
}
.theme--light .bp-row.unseen td {
  border-color: rgba(199, 20, 24, 1);
}
.theme--light .bp-row.unseen td:first-child {
  border-left-color: rgba(199, 20, 24, 1);
}
.theme--light .bp-row.unseen td:last-child {
  border-right-color: rgba(199, 20, 24, 1);
}
.sales-manager-cell { vertical-align: top; }
.sales-manager-list { display:flex; flex-direction:column; gap:0.25rem; }
.sales-manager-row { display:flex; align-items:flex-start; gap:0.4rem; }
.sales-manager-row--dimmed {
  opacity: 1;
  transform: translateX(24px);
}

.sales-manager-list--has-dimmed .sm-wrapper.nav.layout-row.isSalesAgent {
  padding-bottom: 0;
}

.sales-manager-list--has-dimmed .sales-manager-row:not(:first-child):not(:last-child) .sm-wrapper.nav.layout-row.isSalesAgent {
  padding: 0;
}

.sales-manager-list--has-dimmed .sales-manager-row:last-child .sm-wrapper.nav.layout-row.isSalesAgent {
  padding-top: 0;
  padding-bottom: 24px;
}
.sales-manager-text { display:flex; flex-direction:column; gap:0.1rem; }
.sales-manager-name { font-size:0.75rem; font-weight:600; line-height:1.1; }
.sales-manager-status { font-size:0.7rem; color:#64748b; line-height:1.1; }
.sales-manager-empty { font-size:0.75rem; color:#94a3b8; }
/* Status column should stay compact so the name column can take remaining width */
.status-col-header,
.status-col-cell {
  width: 1%;
  white-space: nowrap;
}
table tbody tr.recent-focus.recent-focus-fade { transition: background 1.2s ease, box-shadow 1.2s ease, filter 1.2s ease; }
/* Pagination-Info neben Mehr laden Button */
.load-more-row {
  display: flex;
  align-items: center;
  gap: 1.5rem;
  margin-top: 1.2rem;
  position: absolute;
  bottom: -64px;
  right: 0;
  width: max-content;
}
.pagination-info {
  font-size: 1em;
  color: #64748b;
  font-weight: 500;
}
/* Name column: default like widget (ellipsis), but allow full width when only few columns are visible */
.name-col-cell .infoBusinessPartner p.businessPartner {
  max-width: 320px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.name-col-cell--wide .infoBusinessPartner p.businessPartner {
  max-width: none;
  overflow: visible;
  white-space: normal;
  text-overflow: initial;
}

/* Fallback: Wenn effektiv nur Status- und Namensspalte gerendert werden (2 Spalten),
   soll der Name immer die volle Breite nutzen, auch falls die JS-Klasse nicht greift. */
.bp-row td.status-col-cell:first-child + td.name-col-cell:last-child .infoBusinessPartner p.businessPartner {
  max-width: none;
  overflow: visible;
  white-space: normal;
  text-overflow: initial;
}
</style>
<style scoped>
.spinner-center {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
}
</style>
<style scoped>
/* Google link icon: inline left of the address text */
.google-link-icon {
  display: inline-block;
  width: 16px;
  height: 16px;
  vertical-align: middle;
  object-fit: contain;
}
/* Ensure address links align icon and text horizontally */
.infoAdress a,
.card-body li a {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
}
</style>
<style scoped>
.disabled-link {
  pointer-events: none;
  opacity: 0.6;
  filter: grayscale(0.2);
  cursor: not-allowed;
}
</style>